import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';

import { SHOW_GLOBAL_LOADER } from '../../data-access-ui';
import { PaymentScope, TPaymentScope } from '../enums/payment-scope.enum';
import { CheckoutRequestMetadata, CreateCheckoutRequest } from '../models/create-checkout-request.model';
import { PaymentData } from '../models/payment-data.model';
import { Product } from '../models/product.model';

@Injectable()
export class PaymentService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiUrl;

  getProducts(type: 'SUBSCRIPTION' | 'TOP_UP' | 'REPORT_PRIORITY' | 'CYBER_EXPOSURE_REPORT'): Observable<Product[]> {
    const params = new HttpParams().set('productType', type);

    return this.http.get<Product[]>(`${this.baseUrl}/v1/payment/products`, {
      params,
      context: new HttpContext().set(SHOW_GLOBAL_LOADER, true),
    });
  }

  getUserPaymentData(): Observable<PaymentData> {
    return this.http.get<PaymentData>(`${this.baseUrl}/v1/payment/data`, {
      context: new HttpContext().set(SHOW_GLOBAL_LOADER, true),
    });
  }

  createCheckout(payload: CreateCheckoutRequest): Observable<{ url?: string }> {
    return this.http.post<{ url?: string }>(`${this.baseUrl}/v1/payment/checkout`, payload, {
      context: new HttpContext().set(SHOW_GLOBAL_LOADER, true),
    });
  }

  createPortalSession(customerId: string, returnUrl: string): Observable<{ url?: string }> {
    return this.http.post<{ url?: string }>(
      `${this.baseUrl}/v1/payment/customerportal/session`,
      { customerId, returnUrl },
      {
        context: new HttpContext().set(SHOW_GLOBAL_LOADER, true),
      },
    );
  }

  confirmCheckout(checkoutId: string): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/v1/payment/confirm`,
      { checkoutId },
      {
        context: new HttpContext().set(SHOW_GLOBAL_LOADER, true),
      },
    );
  }

  confirmReportCheckout(checkoutId: string, reportId: string): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/v1/payment/report/confirm`,
      { checkoutId, reportId },
      {
        context: new HttpContext().set(SHOW_GLOBAL_LOADER, true),
      },
    );
  }

  createReturnFromPortalUrl(): string {
    const baseUrl = window.location.origin;

    return `${baseUrl}/dashboard`;
  }

  createCancelUrl(): string {
    const baseUrl = window.location.origin;

    return `${baseUrl}/dashboard`;
  }

  createSuccessUrl(scope: TPaymentScope, metadata?: Partial<CheckoutRequestMetadata>): string {
    const baseUrl = window.location.origin;
    let successUrl = baseUrl;
    let params = new HttpParams().set('scope', scope);

    if (metadata) {
      Object.entries(metadata).forEach(([key, value]) => {
        params = params.set(key, value);
      });
    }

    switch (scope) {
      case PaymentScope.PLAN:
      case PaymentScope.TOP_UP:
      case PaymentScope.REPORT_UPSELL:
        successUrl += '/dashboard?checkout={CHECKOUT_SESSION_ID}';
        break;
      default:
        successUrl += '?checkout={CHECKOUT_SESSION_ID}';
        break;
    }

    return successUrl + '&' + params.toString();
  }
}
